import { useState } from 'react'
import { format, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns'
import { CalendarIcon, Plus, Trash2 } from 'lucide-react'
import { DateRange } from 'react-day-picker'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Button } from '@/common/ui/button'
import { Calendar } from '@/common/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/common/ui/popover'
import { Input } from '@/common/ui/input'
import { Label } from '@/common/ui/label'
import { ScrollArea } from '@/common/ui/scroll-area'
import { useMutationExportPartnerPlayersCsv } from '@/modules/manage-player/api/hooks/useMutationExportPartnerPlayersCsv'
import type { DateRange as DateRangeType } from '@/modules/manage-player/type'

interface PartnerPlayersExportDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	partnerId: string
	phone?: string
}

interface DateRangeItem {
	id: string
	startDate: string
	endDate: string
	label: string
}

const PRESET_RANGES = [
	{
		label: 'วันนี้',
		getValue: () => {
			const today = new Date()
			return {
				startDate: format(today, 'yyyy-MM-dd'),
				endDate: format(today, 'yyyy-MM-dd'),
			}
		},
	},
	{
		label: '7 วันที่ผ่านมา',
		getValue: () => {
			const today = new Date()
			return {
				startDate: format(subDays(today, 7), 'yyyy-MM-dd'),
				endDate: format(today, 'yyyy-MM-dd'),
			}
		},
	},
	{
		label: '30 วันที่ผ่านมา',
		getValue: () => {
			const today = new Date()
			return {
				startDate: format(subDays(today, 30), 'yyyy-MM-dd'),
				endDate: format(today, 'yyyy-MM-dd'),
			}
		},
	},
	{
		label: 'เดือนนี้',
		getValue: () => {
			const today = new Date()
			return {
				startDate: format(startOfMonth(today), 'yyyy-MM-dd'),
				endDate: format(endOfMonth(today), 'yyyy-MM-dd'),
			}
		},
	},
	{
		label: 'เดือนที่แล้ว',
		getValue: () => {
			const lastMonth = subMonths(new Date(), 1)
			return {
				startDate: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
				endDate: format(endOfMonth(lastMonth), 'yyyy-MM-dd'),
			}
		},
	},
]

export default function PartnerPlayersExportDialog({
	open,
	onOpenChange,
	partnerId,
	phone,
}: PartnerPlayersExportDialogProps) {
	const [ranges, setRanges] = useState<DateRangeItem[]>([])
	const [currentRange, setCurrentRange] = useState<DateRange | undefined>()
	const [currentLabel, setCurrentLabel] = useState('')
	const [isCalendarOpen, setIsCalendarOpen] = useState(false)

	const exportMutation = useMutationExportPartnerPlayersCsv()

	const handleAddRange = () => {
		if (currentRange?.from && currentRange?.to) {
			const newRange: DateRangeItem = {
				id: crypto.randomUUID(),
				startDate: format(currentRange.from, 'yyyy-MM-dd'),
				endDate: format(currentRange.to, 'yyyy-MM-dd'),
				label:
					currentLabel ||
					`${format(currentRange.from, 'dd/MM/yyyy')} - ${format(currentRange.to, 'dd/MM/yyyy')}`,
			}
			setRanges([...ranges, newRange])
			setCurrentRange(undefined)
			setCurrentLabel('')
		}
	}

	const handleAddPreset = (preset: (typeof PRESET_RANGES)[0]) => {
		const { startDate, endDate } = preset.getValue()
		const newRange: DateRangeItem = {
			id: crypto.randomUUID(),
			startDate,
			endDate,
			label: preset.label,
		}
		setRanges([...ranges, newRange])
	}

	const handleRemoveRange = (id: string) => {
		setRanges(ranges.filter((r) => r.id !== id))
	}

	const handleExport = () => {
		if (ranges.length === 0) return

		const exportRanges: DateRangeType[] = ranges.map((r) => ({
			startDate: r.startDate,
			endDate: r.endDate,
			label: r.label.replace(/[^a-zA-Z0-9ก-๙\s_-]/g, '_'),
		}))

		exportMutation.mutate(
			{
				partnerId,
				payload: {
					ranges: exportRanges,
					phone: phone || undefined,
				},
			},
			{
				onSuccess: () => {
					onOpenChange(false)
					setRanges([])
				},
			},
		)
	}

	const handleClose = () => {
		onOpenChange(false)
		setRanges([])
		setCurrentRange(undefined)
		setCurrentLabel('')
	}

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="max-w-2xl">
				<DialogHeader>
					<DialogTitle>ส่งออกข้อมูลผู้เล่น (หลายช่วงเวลา)</DialogTitle>
					<DialogDescription>
						เลือกช่วงเวลาที่ต้องการส่งออก ระบบจะสร้างไฟล์ ZIP ที่มี CSV
						แยกตามแต่ละช่วงเวลา
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					{/* Preset Buttons */}
					<div className="space-y-2">
						<Label>เพิ่มช่วงเวลาด่วน</Label>
						<div className="flex flex-wrap gap-2">
							{PRESET_RANGES.map((preset) => (
								<Button
									key={preset.label}
									variant="outline"
									size="sm"
									onClick={() => handleAddPreset(preset)}
								>
									{preset.label}
								</Button>
							))}
						</div>
					</div>

					{/* Custom Range Picker */}
					<div className="space-y-2">
						<Label>หรือเลือกช่วงเวลาเอง</Label>
						<div className="flex flex-col gap-2 sm:flex-row sm:items-end">
							<Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
								<PopoverTrigger asChild>
									<Button
										variant="outline"
										className="w-full justify-start text-left font-normal sm:w-auto"
									>
										<CalendarIcon className="mr-2 h-4 w-4" />
										{currentRange?.from && currentRange?.to ? (
											`${format(currentRange.from, 'dd/MM/yyyy')} - ${format(currentRange.to, 'dd/MM/yyyy')}`
										) : (
											<span>เลือกช่วงวันที่</span>
										)}
									</Button>
								</PopoverTrigger>
								<PopoverContent className="w-auto p-0" align="start">
									<Calendar
										mode="range"
										selected={currentRange}
										onSelect={setCurrentRange}
										numberOfMonths={2}
									/>
								</PopoverContent>
							</Popover>
							<Input
								placeholder="ชื่อช่วงเวลา (ไม่บังคับ)"
								value={currentLabel}
								onChange={(e) => setCurrentLabel(e.target.value)}
								className="w-full sm:w-48"
							/>
							<Button
								onClick={handleAddRange}
								disabled={!currentRange?.from || !currentRange?.to}
								size="sm"
							>
								<Plus className="mr-1 h-4 w-4" />
								เพิ่ม
							</Button>
						</div>
					</div>

					{/* Selected Ranges List */}
					{ranges.length > 0 && (
						<div className="space-y-2">
							<Label>ช่วงเวลาที่เลือก ({ranges.length} ช่วง)</Label>
							<ScrollArea className="h-48 rounded-md border p-2">
								<div className="space-y-2">
									{ranges.map((range) => (
										<div
											key={range.id}
											className="flex items-center justify-between rounded-md bg-muted p-2"
										>
											<div>
												<span className="font-medium">{range.label}</span>
												<span className="ml-2 text-sm text-muted-foreground">
													({range.startDate} - {range.endDate})
												</span>
											</div>
											<Button
												variant="ghost"
												size="sm"
												onClick={() => handleRemoveRange(range.id)}
											>
												<Trash2 className="h-4 w-4 text-destructive" />
											</Button>
										</div>
									))}
								</div>
							</ScrollArea>
						</div>
					)}
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={handleClose}>
						ยกเลิก
					</Button>
					<Button
						onClick={handleExport}
						disabled={ranges.length === 0 || exportMutation.isPending}
					>
						{exportMutation.isPending
							? 'กำลังส่งออก...'
							: `ส่งออก (${ranges.length} ไฟล์)`}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
